import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Bonus, Freespin } from '@/api/common';
import { GameV1 } from '@/api/games';
import { amountWithCurrencySymbol } from '@/helpers/currency';
import { formatDateTime } from '@/helpers/dateTime';
import { getCurrencyCentsAndCode } from '@/helpers/wallet';
import {
  getBonusStatusNameTranslation,
  isBonus,
  PERCENTS_100,
} from '@/modules/promotions/helpers';
import { useCommonStore } from '@/store';

const formatDate = formatDateTime('dd.MM.yyyy / HH:mm:ss');

export const useFormatBonusTerms = (
  bonus: Bonus | Freespin,
  bonusGames?: GameV1[]
) => {
  const { t, i18n } = useTranslation('bonuses');

  const currencies = useCommonStore.use.currencies();

  const {
    validUntil,
    currency,
    activatable,
    activatableUntil,
    createdAt,
    stage,
  } = bonus;

  const {
    amountCents,
    amountWagerCents,
    amountWagerRequirementCents,
    freespinsTotal,
  } = useMemo(
    () =>
      isBonus(bonus)
        ? {
            amountCents: bonus.amountCents,
            amountWagerCents: bonus.amountWagerCents,
            amountWagerRequirementCents: bonus.amountWagerRequirementCents,
          }
        : {
            freespinsTotal: bonus.freespinsTotal,
            gamesInfo: bonus.gamesInfo,
          },
    [bonus]
  );

  const bonusFormatedData = useMemo(() => {
    const amountWagerData =
      amountWagerCents !== undefined
        ? getCurrencyCentsAndCode(amountWagerCents, currency, currencies)
        : undefined;

    const wagerRequirementData =
      amountWagerRequirementCents !== undefined
        ? getCurrencyCentsAndCode(
            amountWagerRequirementCents,
            currency,
            currencies
          )
        : undefined;

    const bonusAmountData =
      amountCents !== undefined
        ? getCurrencyCentsAndCode(amountCents, currency, currencies)
        : undefined;

    const status = getBonusStatusNameTranslation(
      t,
      i18n,
      stage,
      true
    ) as string;

    return {
      dateReceived: formatDate(createdAt),
      activateUntilDate:
        activatable && activatableUntil
          ? formatDate(activatableUntil)
          : undefined,
      expireDate:
        !activatable && validUntil ? formatDate(validUntil) : undefined,
      wager:
        amountWagerRequirementCents && amountCents
          ? `${amountWagerRequirementCents / amountCents}x`
          : undefined,
      amountWager: amountWagerData
        ? amountWithCurrencySymbol(
            amountWagerData.currency,
            amountWagerData.amount
          )
        : undefined,
      wagerRequirement: wagerRequirementData
        ? amountWithCurrencySymbol(
            wagerRequirementData.currency,
            wagerRequirementData.amount
          )
        : undefined,
      wageredPercent:
        amountWagerRequirementCents && amountWagerCents !== undefined
          ? `${Math.floor(
              (amountWagerCents * PERCENTS_100) / amountWagerRequirementCents
            )}%`
          : undefined,
      bonusAmount: bonusAmountData
        ? amountWithCurrencySymbol(
            bonusAmountData.currency,
            bonusAmountData.amount
          )
        : undefined,
      bonusFreespins: freespinsTotal ? `${freespinsTotal}x` : undefined,
      bonusGamesLinks: bonusGames?.length ? (
        <>
          {bonusGames.map(({ title, fullPath }, i) => (
            <a href={`game/${fullPath}`} key={i}>
              {title}
              {i !== bonusGames.length - 1 ? ', ' : ''}
            </a>
          ))}
        </>
      ) : (
        ''
      ),
      status,
    };
  }, [
    activatable,
    activatableUntil,
    amountCents,
    amountWagerCents,
    amountWagerRequirementCents,
    createdAt,
    currencies,
    currency,
    freespinsTotal,
    bonusGames,
    i18n,
    stage,
    t,
    validUntil,
  ]);

  return bonusFormatedData;
};
